import React from "react";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Form } from "tsi-common-react/src/forms/Form";
import { FormNumber } from "tsi-common-react/src/forms/FormNumber";
import { FormSelect } from "tsi-common-react/src/forms/FormSelect";
import { FormSubmit } from "tsi-common-react/src/forms/FormSubmit";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import { getDinero, getMonthlyPrice } from "tsi-common-react/src/utils/money";
import { t } from "ttag";

const getTermMonths = () => {
    const plans = listFinancingPlans();

    return plans.map((plan) => {
        const planLength = isoMonths.unwrap(plan.length);

        return {
            // TODO(i18n): Use ngettext for plurals: ngettext(msgid`${planLength} Month`, `${planLength} Months`, planLength)
            label: `${planLength} Months`,
            value: `${planLength}`,
        };
    });
};

const calculatePayment = (paymentAmount: string, paymentTerm: string) => {
    return {
        paymentTotal: format.money(
            getMonthlyPrice(
                getDinero(paymentAmount),
                parseInt(paymentTerm, 10),
            ),
        ),
        paymentAmountCalcd: String(parseInt(paymentAmount, 10).toFixed(2)),
        paymentTermCalcd: paymentTerm,
    };
};

export const FinanceCalculator = (): React.ReactNode => {
    const [paymentAmount, setPaymentAmount] = React.useState<string>("4000");
    const [paymentTerm, setPaymentTerm] = React.useState<string>("24");
    const [paymentAmountCalcd, setPaymentAmountCalcd] =
        React.useState<string>("4000.00");
    const [paymentTermCalcd, setPaymentTermCalcd] =
        React.useState<string>("24");
    const [paymentTotal, setPaymentTotal] = React.useState<string>("83.33");

    React.useEffect(() => {
        const plans = listFinancingPlans();
        if (plans.length > 0) {
            const bestPlan = plans[plans.length - 1];
            const bestPlanLength = isoMonths.unwrap(bestPlan.length);

            const calculated = calculatePayment(
                paymentAmount,
                String(bestPlanLength),
            );

            setPaymentTerm(String(bestPlanLength));
            setPaymentTermCalcd(calculated.paymentTermCalcd);
            setPaymentTotal(calculated.paymentTotal);
        }
    }, []);

    const onCalcPayment = (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();

        const calculated = calculatePayment(paymentAmount, paymentTerm);

        setPaymentTotal(calculated.paymentTotal);
        setPaymentAmountCalcd(calculated.paymentAmountCalcd);
        setPaymentTermCalcd(calculated.paymentTermCalcd);

        return Promise.resolve();
    };

    const onPaymentChange = (event: React.FormEvent<HTMLInputElement>) => {
        setPaymentAmount(event.currentTarget.value);
    };

    const onTermChange = (event: React.FormEvent<HTMLSelectElement>) => {
        setPaymentTerm(event.currentTarget.value);
    };

    return (
        <div className="finance-calculator">
            <h3 className="finance-calculator__title">
                {t`Monthly Payment Calculator`}
            </h3>
            <Form className="finance-calculator-form" onSubmit={onCalcPayment}>
                <fieldset>
                    <legend className="ada-screenreader-only">
                        {t`Calculate Monthly Payment`}
                    </legend>
                    <FormNumber
                        name="finance-payment"
                        // TODO(i18n): Wrap in t tagged template: label={t`Enter Financed Amount`}
                        label="Enter Financed Amount"
                        id="finance-payment"
                        className="finance-calculator-form__payment"
                        onChange={onPaymentChange}
                        value={paymentAmount}
                        min={1}
                        max={1000000}
                    />
                    <p className="finance-calculator-form__for">{t`for`}</p>
                    <FormSelect
                        name="finance-terms"
                        id="finance-terms"
                        // TODO(i18n): Wrap in t tagged template: label={t`Term Length Dropdown`}
                        label="Term Length Dropdown"
                        className="finance-calculator-form__terms"
                        value={paymentTerm}
                        onChange={onTermChange}
                        choices={getTermMonths()}
                    />
                    <FormSubmit
                        // TODO(i18n): Wrap in t tagged template: value={t`Calculate Monthly Payment`}
                        value="Calculate Monthly Payment"
                        className="finance-calculator-form__submit al-financing-page__calculator--calc-payment"
                    />
                </fieldset>
            </Form>
            <div className="finance-calculator__payment">
                {/* TODO(i18n): Use jt to keep sentence together with interpolated components instead of splitting into fragments */}
                <a href="#terms_and_conditions">
                    <span className="finance-calculator__payment--underlined">
                        {paymentTotal}/{t`mo`}.
                    </span>
                    <sup>2</sup>
                    <span className="ada-screenreader-only">
                        {t`Additional information about Terms and Conditions`}
                    </span>
                </a>
                &nbsp;{t`for`}&nbsp;
                <a href="#terms_and_conditions">
                    <span className="finance-calculator__payment--underlined">
                        {/* TODO(i18n): Use ngettext for plurals: ngettext(msgid`${paymentTermCalcd} month`, `${paymentTermCalcd} months`, parseInt(paymentTermCalcd, 10)) */}
                        {paymentTermCalcd} {t`months`}
                    </span>
                    <sup>1</sup>
                    <span className="ada-screenreader-only">
                        {t`Additional information about Terms and Conditions`}
                    </span>
                </a>
                <br />
                {t`(total payments of $${paymentAmountCalcd})`}
            </div>
            <p className="finance-calculator__disclaimer">
                {t`Check table for details about minimum financing requirements`}
            </p>
            <FinancingModalTrigger
                modalType="prequal-app"
                className="button button--inverse finance-calculator__button al-financing-page__calculator--check-prequalify"
            >
                {t`Check if you Pre-Qualify`}
            </FinancingModalTrigger>
        </div>
    );
};
